<template>
  <div
    ref="refSwiperContainer"
    class="review-card-swiper-media-container"
  >
    <!-- Skeleton пока Swiper не загружен -->
    <div 
      v-if="!swiperLoaded"
      class="media-swiper-skeleton"
    >
      <div 
        v-for="i in Math.min(media.length, 3)" 
        :key="`skeleton-${i}`"
        class="skeleton-media-item"
      />
    </div>

    <!-- Swiper загружается только когда виден -->
    <div
      v-show="swiperLoaded"
      ref="refSwiper"
      class="review-card-swiper-media swiper"
    >
      <div class="swiper-wrapper">
        <div
          v-for="(item, index) in media"
          :key="`swiper-slide-${ index }`"
          class="swiper-slide"
          @click="() => clickItem(index)"
        >
          <template v-if="item.type === 'image'">
            <nuxt-img
              :src="String(item.value)"
              :alt="`Review image ${index + 1}`"
              width="124"
              height="82"
              fit="cover"
              loading="lazy"
              decoding="async"
              fetchpriority="low"
            />
          </template>
          <template v-if="item.type === 'video'">
            <div class="--video">
              <video 
                loop 
                :controls="false"
                preload="none"
                playsinline
              >
                <source :src="item.value" />
              </video>
            </div>
          </template>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import type Swiper from 'swiper';
import { safeDestroySwiper } from '~/utils/swiper/safeDestroySwiper';
import useGalleryComponent from '~/composables/useGallery';

interface IItemMedia {
  type: string;
  value: any
};
interface IProps {
  media: IItemMedia[]
};
const props = withDefaults(defineProps<IProps>(), {
  media: () => []
});

const refSwiper: any = ref(null);
const refSwiperContainer: any = ref(null);
const swiperLoaded = ref(false);
let swiperInstance: Swiper | null = null;

// Динамическая загрузка Swiper только когда он нужен
const initSwiper = async () => {
  if (swiperInstance || !refSwiper.value) return;
  
  try {
    // Динамический импорт Swiper и его модулей
    // CSS Swiper загружается автоматически или добавлен глобально
    const [{ default: SwiperClass }, { Navigation }] = await Promise.all([
      import('swiper'),
      import('swiper/modules')
    ]);

    swiperInstance = new SwiperClass(refSwiper.value, {
      modules: [Navigation],
      breakpoints: {
        1200: {
          slidesPerView: 4
        }
      },
      slidesPerView: 3,
      spaceBetween: 6,
      watchSlidesProgress: true,
      observer: true,
      observeParents: true,
    });
    
    swiperLoaded.value = true;
  } catch (error) {
    console.error('Failed to initialize media Swiper:', error);
  }
};

// Intersection Observer для lazy loading
let observer: IntersectionObserver | null = null;

onMounted(() => {
  if (!process.client || !refSwiperContainer.value) return;

  // Загружаем Swiper только когда блок становится видимым
  observer = new IntersectionObserver(
    (entries) => {
      entries.forEach((entry) => {
        if (entry.isIntersecting && !swiperLoaded.value) {
          initSwiper();
          observer?.disconnect();
        }
      });
    },
    {
      rootMargin: '100px',
      threshold: 0.1
    }
  );

  observer.observe(refSwiperContainer.value);
});

onBeforeUnmount(() => {
  safeDestroySwiper(swiperInstance);
  swiperInstance = null;

  if (observer) {
    observer.disconnect();
    observer = null;
  }
});

const { openGallery } = useGalleryComponent();
const clickItem = (index: number) => {
  openGallery({
    imagesIds: props.media.filter((t) => t.type === 'image').map((t) => t.value),
    videos: props.media.filter((t) => t.type === 'video').map((t) => {
      return {url: t.value}
    }),
    indexActiveSlide: index
  });
}
</script>

<style lang="scss">
@import 'swiper/css';
@import 'swiper/css/navigation';

.review-card-swiper-media-container {
  position: relative;
  min-height: 82px; // Предотвращение CLS
}

// Skeleton loader для медиа Swiper
.media-swiper-skeleton {
  display: flex;
  gap: 6px;
}

.skeleton-media-item {
  width: calc(33.333% - 4px);
  aspect-ratio: 124/82;
  border-radius: 6px;
  background: linear-gradient(
    90deg,
    #e8e8e8 25%,
    #d4d4d4 50%,
    #e8e8e8 75%
  );
  background-size: 200% 100%;
  animation: shimmer 1.5s infinite;
  
  @media (min-width: 1200px) {
    width: calc(25% - 4.5px);
  }
}

@keyframes shimmer {
  0% {
    background-position: 200% 0;
  }
  100% {
    background-position: -200% 0;
  }
}

.review-card-swiper-media {
  position: relative;
  overflow: hidden;
  
  .swiper-slide {
    cursor: pointer;
    will-change: transform; // Performance optimization
    
    & > *, video {
      position: absolute;
      top: 0; left: 0;
      width: 100%; height: 100%;
      object-fit: cover;
      border-radius: 6px;
    }
    
    &:after {
      content: "";
      float: left;
      padding-top: 66%;
    }

    .--video {
      position: relative;
      
      &:before {
        content: "";
        position: absolute;
        top: 50%; left: 50%;
        transform: translate(-50%, -50%);
        width: 24px; height: 24px;
        z-index: 2;
        background-image: url("@/assets/img/svg/common/play.svg");
        background-size: contain;
        background-repeat: no-repeat;
        background-position: center;
        filter: invert(1);
        pointer-events: none;
      }
    }
  }
}
</style>
